import type { SimulationState } from '../types';
import { syncStaffIdCounter } from './StaffFactory';
import { syncTeamIdCounter } from './TeamFactory';
import { syncProjectIdCounter } from './ProjectFactory';
import { syncContractIdCounter } from './ContractFactory';

/**
 * After loading a save, deduplicate any colliding entity IDs (from older
 * save files created before the counter-sync fix), then sync all ID
 * counters to the max existing ID so newly created entities are unique.
 */
export function syncAllCounters(state: SimulationState): void {
  deduplicateIds(state);
  syncStaffIdCounter([...state.staff, ...state.hiringPool]);
  syncTeamIdCounter(state.teams);
  syncProjectIdCounter(state.projects);
  syncContractIdCounter([...state.contracts, ...state.contractPool]);
}

/**
 * Fix corrupted saves where multiple entities share the same ID.
 * Reassigns fresh unique IDs to duplicates and patches all references.
 */
function deduplicateIds(state: SimulationState): void {
  // Teams — fix duplicate team IDs and update staff.teamId references
  const seenTeamIds = new Set<string>();
  let teamCounter = 0;
  // First pass: find the max existing counter value
  for (const t of state.teams) {
    const n = parseInt(t.id.replace('team_', ''), 10);
    if (n > teamCounter) teamCounter = n;
  }
  // Second pass: reassign duplicates
  for (const t of state.teams) {
    if (seenTeamIds.has(t.id)) {
      const newId = `team_${++teamCounter}`;
      // Only patch staff who are actually members of THIS team
      const memberSet = new Set(t.memberIds);
      for (const s of state.staff) {
        if (memberSet.has(s.id)) s.teamId = newId;
      }
      // Patch project assignment if it referenced the old team
      if (t.assignedProjectId) {
        for (const p of state.projects) {
          if (p.assignedTeamId === t.id) p.assignedTeamId = newId;
        }
      }
      t.id = newId;
    }
    seenTeamIds.add(t.id);
  }

  // Staff — fix duplicate staff IDs
  const seenStaffIds = new Set<string>();
  let staffCounter = 0;
  for (const s of [...state.staff, ...state.hiringPool]) {
    const n = parseInt(s.id.replace('staff_', ''), 10);
    if (n > staffCounter) staffCounter = n;
  }
  for (const s of state.staff) {
    if (seenStaffIds.has(s.id)) {
      const oldId = s.id;
      const newId = `staff_${++staffCounter}`;
      s.id = newId;
      // Patch team memberIds and leaderId
      for (const t of state.teams) {
        const idx = t.memberIds.indexOf(oldId);
        if (idx !== -1) t.memberIds[idx] = newId;
        if (t.leaderId === oldId) t.leaderId = newId;
      }
      // Patch contract references
      for (const c of state.contracts) {
        const aIdx = c.assignedStaffIds.indexOf(oldId);
        if (aIdx !== -1) c.assignedStaffIds[aIdx] = newId;
        const lIdx = c.lockedStaffIds.indexOf(oldId);
        if (lIdx !== -1) c.lockedStaffIds[lIdx] = newId;
      }
    }
    seenStaffIds.add(s.id);
  }
}
